import dayjs, { Dayjs } from 'dayjs';
import { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';

import { TokensChip } from '@/Pages/Run/components/TokensChip';
import { ElapsedTime } from '@/components/ElapsedTime';
import { FeedbackChips } from '@/components/FeedbackChips';
import { RunFeedbackButtons } from '@/components/RunFeedbackButtons';
import { RunLatencyChip } from '@/components/RunLatencyChip';
import { Tooltip } from '@/design-system';
import ExternalLinkIcon from '@/icons/ExternalLinkIcon.svg?react';
import { RunSchema } from '@/types/schema';
import { localTime } from '@/utils/utc-time';

function PlaygroundStatsDate(props: { date: Date | Dayjs }) {
  return (
    <Tooltip title={dayjs(props.date).format('MM/DD/YYYY, hh:mm:ss A')}>
      <span className="text-sm">
        <ElapsedTime date={props.date} />
      </span>
    </Tooltip>
  );
}

function PlaygroundStatsLink(props: { href?: string; children: ReactNode }) {
  if (props.href == null) return <>{props.children}</>;
  return (
    <Link
      to={props.href}
      className="flex items-center gap-1 hover:underline"
      data-testid="playground-stats-link"
    >
      {props.children}
      <ExternalLinkIcon className="h-4 w-4" />
    </Link>
  );
}

export function PlaygroundStats({ run }: { run?: RunSchema }) {
  const { datasetShareToken } = useParams();
  return (
    <div className="flex flex-wrap items-center gap-2">
      <FeedbackChips
        feedbackStats={run?.feedback_stats}
        feedbackSourceRunId={run?.id}
      />

      {run && <RunLatencyChip run={run} />}

      <TokensChip run={run} isLoading={run?.total_tokens == null} />

      {run && !datasetShareToken && <RunFeedbackButtons size="xs" run={run} />}

      {run && (
        <PlaygroundStatsLink href={run?.app_path}>
          <PlaygroundStatsDate date={localTime(run.start_time)} />
        </PlaygroundStatsLink>
      )}
    </div>
  );
}

export const PlaygroundRunLink = ({ run }: { run: RunSchema }) => {
  return (
    <PlaygroundStatsLink href={run.app_path}>
      <PlaygroundStatsDate date={localTime(run.start_time)} />
    </PlaygroundStatsLink>
  );
};
